// state-vault.js
// Encrypted-at-rest storage for what has to survive a reload: the MLS group
// state (which holds private key material) and the conversation history
// (plaintext messages). Both are encrypted with AES-256-GCM under a key
// derived from the unlocked identity key, so they are protected exactly as
// strongly as the identity itself — the passphrase / WebAuthn unlock that
// already guards the identity guards this too, and nothing readable sits in
// IndexedDB beside it. A fresh random IV is used for every write.
//
// Each record's name is bound into the ciphertext as additional authenticated
// data, so a blob copied under a different name (the MLS state swapped with
// the message history, say) fails to decrypt rather than being accepted.
//
// If a record can't be decrypted (a different identity, corruption, tampering)
// load() returns null and remembers why, rather than throwing: the app should
// start with empty state, not refuse to start. The unreadable blob is kept
// under "<name>.unreadable" before the next save replaces it, so a bug can't
// silently destroy data that might still be recoverable.

import { IndexedDbStorage } from './storage-browser.js'

const SALT = new TextEncoder().encode('p2p-core')
const INFO = new TextEncoder().encode('state-vault/v1')
const aadFor = (name) => new TextEncoder().encode(`p2p-core/${name}/v1`)

export class StateVault {
  /**
   * @param {{privateKey: {raw: Uint8Array}}} identity - an unlocked identity
   * @param {object} [opts]
   * @param {(name: string) => {load: () => Promise<any>, save: (data: any) => Promise<void>}} [opts.storageFor]
   */
  constructor (identity, { storageFor = (name) => new IndexedDbStorage(name) } = {}) {
    this._identity = identity
    this._storageFor = storageFor
    this._key = null
    /** name -> reason, for records that existed but couldn't be decrypted */
    this.unreadable = new Map()
  }

  async _cryptoKey () {
    if (!this._key) {
      const ikm = await globalThis.crypto.subtle.importKey('raw', this._identity.privateKey.raw, 'HKDF', false, ['deriveKey'])
      this._key = await globalThis.crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: SALT, info: INFO },
        ikm,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      )
    }
    return this._key
  }

  /** @returns {Promise<any|null>} the saved object, or null if none / unreadable */
  async load (name) {
    const record = await this._storageFor(name).load()
    if (!record) return null
    try {
      const plain = await globalThis.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: record.iv, additionalData: aadFor(name) },
        await this._cryptoKey(),
        record.ct
      )
      return JSON.parse(new TextDecoder().decode(plain))
    } catch (err) {
      this.unreadable.set(name, err.message || 'decryption failed')
      return null
    }
  }

  async save (name, object) {
    if (this.unreadable.has(name)) {
      // Keep the blob we couldn't read before replacing it.
      const old = await this._storageFor(name).load()
      if (old) await this._storageFor(`${name}.unreadable`).save(old)
      this.unreadable.delete(name)
    }
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12))
    const ct = new Uint8Array(await globalThis.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aadFor(name) },
      await this._cryptoKey(),
      new TextEncoder().encode(JSON.stringify(object))
    ))
    await this._storageFor(name).save({ v: 1, iv, ct })
  }
}
